import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type Distance, DistanceSchemas } from "@workspace/contracts/distance";
import { useState } from "react";
import { orpc } from "@/api/orpc";
import { DataTable } from "@/components/data-table/data-table";
import { DataTableActionBar } from "@/components/data-table/data-table-action-bar";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { CreateDistanceDialog } from "@/features/distance/create";
import { DeleteDistance } from "@/features/distance/delete";
import { UpdateDistanceDialog } from "@/features/distance/update";
import { useDistancesColumns } from "@/features/project/hooks/use-distances-columns";
import { useDataTable } from "@/hooks/use-data-table";
import { Main } from "@/layout/main";
import { withDashboardPaginationDefaults } from "@/lib/pagination";
import type { DataTableRowAction } from "@/types/data-table";

export const Route = createFileRoute("/_authenticated/distances")({
  validateSearch: (search) => DistanceSchemas.list.parse(withDashboardPaginationDefaults(search)),
  component: RouteComponent,
  loaderDeps: ({ search }) => search,
  loader: async ({ deps, context: { orpc, queryClient } }) => {
    await queryClient.ensureQueryData(orpc.distances.list.queryOptions({ input: deps }));
  },
});

function RouteComponent() {
  const search = Route.useSearch();

  const [rowAction, setRowAction] = useState<DataTableRowAction<Distance> | null>(null);

  const columns = useDistancesColumns({ setRowAction });

  const query = useSuspenseQuery(orpc.distances.list.queryOptions({ input: search }));

  const { table } = useDataTable({
    columns: columns,
    data: query.data.items,
    pageCount: query.data.totalPageNumbers,
    debounceMs: 500,
    getRowId: (originalRow) => originalRow.id,
    enableSorting: false,
  });

  return (
    <Main>
      <DataTable table={table} actionBar={<DataTableActionBar table={table} />}>
        <DataTableToolbar table={table}>
          <CreateDistanceDialog />
        </DataTableToolbar>
      </DataTable>

      <UpdateDistanceDialog
        distance={rowAction?.row.original}
        open={rowAction?.variant === "update"}
        onClose={() => setRowAction(null)}
      />

      <DeleteDistance
        distance={rowAction?.row.original}
        open={rowAction?.variant === "delete"}
        onClose={() => setRowAction(null)}
      />
    </Main>
  );
}
